import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  LARGE_IMAGE_CHARS,
  fileLabel,
  formatBytes,
  imageSrc,
  mimeLabel,
  safeStringify,
  type MediaPart,
} from '@/utils/traceContent';

type ImagePart = Extract<MediaPart, { kind: 'image' }>;

const FileGlyph = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
    />
  </svg>
);

const ImageGlyph = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A1.5 1.5 0 0021.75 19.5V4.5A1.5 1.5 0 0020.25 3H3.75A1.5 1.5 0 002.25 4.5v15A1.5 1.5 0 003.75 21zm10.5-11.25h.008v.008h-.008V9.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
    />
  </svg>
);

/** 文件、Langfuse 媒体引用或无法预览图片的卡片 */
function AttachmentCard({ icon, title, subtitle, hint }: { icon: 'file' | 'image'; title: string; subtitle: string; hint?: string }) {
  const Glyph = icon === 'file' ? FileGlyph : ImageGlyph;
  return (
    <div className="flex items-center gap-2 rounded-md border bg-background/60 px-2.5 py-2 max-w-[260px]" title={hint}>
      <Glyph className="w-6 h-6 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <div className="text-xs font-medium truncate">{title}</div>
        <div className="text-[11px] text-muted-foreground truncate">{subtitle}</div>
      </div>
    </div>
  );
}

/** 大图弹层，渲染在 body 上以覆盖抽屉 */
function ImageLightbox({ part, src, onClose }: { part: ImagePart; src: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-black/80 p-6" onClick={onClose}>
      <img
        src={src}
        alt={part.name}
        className="max-w-[90vw] max-h-[80vh] object-contain rounded shadow-2xl bg-white"
        onClick={e => e.stopPropagation()}
      />
      <div className="mt-3 flex items-center gap-3 text-sm text-white/90" onClick={e => e.stopPropagation()}>
        <span className="truncate max-w-[50vw]">{part.name}</span>
        <span className="text-white/60">
          {mimeLabel(part.mime)} {formatBytes(part.size)}
        </span>
        <a href={src} download={part.name} className="underline hover:text-white">
          下载
        </a>
        <button onClick={onClose} className="underline hover:text-white">
          关闭
        </button>
      </div>
    </div>,
    document.body
  );
}

/** 图片缩略图；过大的图片点击后才加载 */
function TraceImage({ part }: { part: ImagePart }) {
  const [loadLarge, setLoadLarge] = useState(false);
  const [open, setOpen] = useState(false);
  const src = useMemo(() => imageSrc(part), [part]);
  const subtitle = [mimeLabel(part.mime), formatBytes(part.size)].filter(Boolean).join(' · ');

  if (!src) {
    const url = part.url || part.sourceUrl;
    if (!url) return <AttachmentCard icon="image" title={part.name} subtitle={subtitle} />;
    return (
      <div className="flex w-full items-start gap-1.5 text-xs text-muted-foreground" title={part.name}>
        <ImageGlyph className="w-4 h-4 shrink-0" />
        <span className="font-mono break-all select-all">{url}</span>
      </div>
    );
  }
  const isLarge = (part.base64?.length ?? part.url?.length ?? 0) > LARGE_IMAGE_CHARS;
  if (isLarge && !loadLarge) {
    return (
      <button
        onClick={() => setLoadLarge(true)}
        className="flex h-24 w-32 flex-col items-center justify-center gap-1 rounded-md border bg-muted/50 text-xs text-muted-foreground hover:bg-muted"
      >
        <ImageGlyph className="w-6 h-6" />
        图片 {formatBytes(part.size)}
        <span className="text-blue-600">点击加载</span>
      </button>
    );
  }
  return (
    <figure className="w-32">
      <button onClick={() => setOpen(true)} className="block w-full overflow-hidden rounded-md border bg-muted/30" title="查看大图">
        <img src={src} alt={part.name} loading="lazy" className="h-24 w-full object-cover" />
      </button>
      <figcaption className="mt-1 truncate text-[11px] text-muted-foreground" title={part.name}>
        {part.name} {subtitle && `· ${subtitle}`}
      </figcaption>
      {open && <ImageLightbox part={part} src={src} onClose={() => setOpen(false)} />}
    </figure>
  );
}

function UnknownPart({ part }: { part: Extract<MediaPart, { kind: 'unknown' }> }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="w-full text-xs">
      <button onClick={() => setOpen(!open)} className="text-muted-foreground hover:text-foreground">
        [{part.type}] 内容已省略，{open ? '收起' : '展开'}
      </button>
      {open && <pre className="mt-1 max-h-60 overflow-auto rounded bg-muted/50 p-2">{safeStringify(part.raw, 2)}</pre>}
    </div>
  );
}

/** 消息中的多模态内容；index 用于把只有地址的附件替换为同 key 的可预览版本 */
export function MediaParts({ parts, index }: { parts: MediaPart[]; index?: Map<string, MediaPart> }) {
  if (parts.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {parts.map((p, i) => {
        const part = index?.get(p.key) ?? p;
        switch (part.kind) {
          case 'image':
            return <TraceImage key={`${part.key}-${i}`} part={part} />;
          case 'file':
            return (
              <AttachmentCard
                key={`${part.key}-${i}`}
                icon="file"
                title={part.name}
                subtitle={[fileLabel(part.name, part.mime), formatBytes(part.size)].filter(Boolean).join(' · ')}
                hint={part.url ?? part.mime}
              />
            );
          case 'media-ref':
            return (
              <AttachmentCard
                key={`${part.key}-${i}`}
                icon="image"
                title="图片（Langfuse 媒体）"
                subtitle={`${mimeLabel(part.mime)} · 暂不支持预览`}
                hint={part.id}
              />
            );
          default:
            return <UnknownPart key={`${part.key}-${i}`} part={part} />;
        }
      })}
    </div>
  );
}

/** 本次对话的附件汇总（已去重） */
export function AttachmentsSummary({ parts }: { parts: MediaPart[] }) {
  if (parts.length === 0) return null;
  return (
    <div className="border rounded-lg">
      <div className="px-4 py-3 text-sm font-medium border-b bg-muted/30 rounded-t-lg">附件 · {parts.length}</div>
      <div className="px-4 pb-4">
        <MediaParts parts={parts} />
      </div>
    </div>
  );
}
